import React from 'react';
import {
  ArrowOutwardRounded,
  CheckCircleRounded,
  PhoneIphoneRounded
} from '@mui/icons-material';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FONT_FAMILY } from 'common/sharedFunctions';
import HandsOnMobile from '../assets/img/handsonmobile.jpg';
import AppStoreBadge from '../assets/img/appstore.png';
import PlayStoreBadge from '../assets/img/playstore.png';

const accent = '#B7EE00';
const ink = '#111411';

const StoreButton = ({ alt, href, image, label }) => (
  <Box
    aria-label={label}
    component="a"
    href={href}
    rel="noreferrer"
    target="_blank"
    sx={{
      alignItems: 'center',
      backgroundColor: ink,
      border: '1px solid rgba(17,20,17,0.92)',
      borderRadius: '14px',
      display: 'inline-flex',
      justifyContent: 'center',
      minHeight: 64,
      px: 2.25,
      transition: 'transform 180ms ease, box-shadow 180ms ease',
      width: { xs: '100%', sm: 'auto' },
      '&:hover': {
        boxShadow: '0 14px 28px rgba(17,20,17,0.2)',
        transform: 'translateY(-3px)'
      },
      '&:focus-visible': {
        outline: '3px solid #FFFFFF',
        outlineOffset: '3px'
      }
    }}
  >
    <Box
      component="img"
      src={image}
      alt={alt}
      sx={{ height: 'auto', maxHeight: 46, maxWidth: '100%', width: 'auto' }}
    />
    <ArrowOutwardRounded sx={{ color: '#FFFFFF', fontSize: 18, ml: 1 }} />
  </Box>
);

const DownloadApp = () => {
  const settings = useSelector((state) => state.settingsdata.settings);
  const { t } = useTranslation();

  const features = [
    t('app_feature_booking', { defaultValue: 'Book your everyday rides quickly' }),
    t('app_feature_tracking', { defaultValue: 'Follow your journey in one place' }),
    t('app_feature_support', { defaultValue: 'Keep trip details close at hand' })
  ];

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: accent,
        color: ink,
        overflow: 'hidden',
        py: { xs: 7, sm: 9, md: 11 },
        position: 'relative'
      }}
    >
      <Typography
        aria-hidden="true"
        sx={{
          bottom: { xs: 8, sm: 2, md: -4 },
          color: 'rgba(17,20,17,0.055)',
          fontFamily: FONT_FAMILY,
          fontSize: { xs: '6rem', sm: '10rem', md: '15rem' },
          fontWeight: 900,
          left: '50%',
          letterSpacing: '-0.035em',
          lineHeight: 0.76,
          pointerEvents: 'none',
          position: 'absolute',
          textAlign: 'center',
          transform: 'translateX(-50%)',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          width: 'max-content'
        }}
      >
        LAIWANI
      </Typography>

      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, position: 'relative' }}>
        <Box sx={{
          alignItems: 'center',
          display: 'grid',
          gap: { xs: 5, md: 7, lg: 10 },
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1fr 0.95fr' }
        }}>
          <Box sx={{ minWidth: 0, order: { xs: 2, md: 1 } }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <PhoneIphoneRounded sx={{ fontSize: 20 }} />
              <Typography sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '0.76rem',
                fontWeight: 900,
                letterSpacing: '0.13em',
                textTransform: 'uppercase'
              }}>
                {t('laiwani_in_your_pocket', { defaultValue: 'Laiwani in your pocket' })}
              </Typography>
            </Stack>

            <Typography component="h2" sx={{
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '2.3rem', sm: '3.2rem', md: '3.65rem' },
              fontWeight: 900,
              letterSpacing: '-0.05em',
              lineHeight: 0.98,
              mb: 2.25,
              maxWidth: 650
            }}>
              {t('mobile_apps_on_store', { defaultValue: 'Take every ride with you.' })}
            </Typography>
            <Typography sx={{
              color: 'rgba(17,20,17,0.72)',
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '0.96rem', sm: '1.05rem' },
              lineHeight: 1.7,
              maxWidth: 590,
              mb: 3
            }}>
              {t('app_store_deception1', {
                defaultValue: 'Book, follow, and manage your rides from the Laiwani app.'
              })}
            </Typography>

            <Stack spacing={1.25} sx={{ mb: 3.5 }}>
              {features.map((feature) => (
                <Stack key={feature} direction="row" spacing={1.1} alignItems="center">
                  <CheckCircleRounded sx={{ fontSize: 20 }} />
                  <Typography sx={{
                    fontFamily: FONT_FAMILY,
                    fontSize: '0.9rem',
                    fontWeight: 700
                  }}>
                    {feature}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            {(settings && settings.AppleStoreLink) || (settings && settings.PlayStoreLink) ? (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                useFlexGap
                sx={{ alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}
              >
                {settings && settings.AppleStoreLink ? (
                  <StoreButton
                    alt="Download on the App Store"
                    href={settings.AppleStoreLink}
                    image={AppStoreBadge}
                    label={t('download_ios_app', { defaultValue: 'Download the iOS app' })}
                  />
                ) : null}
                {settings && settings.PlayStoreLink ? (
                  <StoreButton
                    alt="Get it on Google Play"
                    href={settings.PlayStoreLink}
                    image={PlayStoreBadge}
                    label={t('download_android_app', { defaultValue: 'Download the Android app' })}
                  />
                ) : null}
              </Stack>
            ) : null}
          </Box>

          <Box sx={{ minWidth: 0, order: { xs: 1, md: 2 } }}>
            <Box sx={{
              backgroundColor: ink,
              border: '1px solid rgba(17,20,17,0.2)',
              borderRadius: { xs: '24px', sm: '32px' },
              boxShadow: '0 30px 70px rgba(17,20,17,0.22)',
              margin: '0 auto',
              maxWidth: 530,
              overflow: 'hidden',
              p: { xs: 1.25, sm: 1.75 },
              position: 'relative',
              transform: { md: 'rotate(2deg)' }
            }}>
              <Box sx={{
                alignItems: 'center',
                color: '#FFFFFF',
                display: 'flex',
                justifyContent: 'space-between',
                pb: 1.5,
                px: 0.75,
                pt: 0.25
              }}>
                <Typography sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: '0.72rem',
                  fontWeight: 900,
                  letterSpacing: '0.1em'
                }}>
                  LAIWANI APP
                </Typography>
                <Box sx={{
                  backgroundColor: accent,
                  borderRadius: '50%',
                  boxShadow: '0 0 0 4px rgba(183,238,0,0.14)',
                  height: 8,
                  width: 8
                }} />
              </Box>
              <Box
                component="img"
                src={HandsOnMobile}
                alt={t('mobile_app_preview', { defaultValue: 'Laiwani mobile app preview' })}
                sx={{
                  aspectRatio: '16 / 10.2',
                  borderRadius: { xs: '17px', sm: '23px' },
                  display: 'block',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  width: '100%'
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default DownloadApp;
